import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, IndianRupee, CheckCircle2, QrCode, Calendar, Copy, Check, Wallet, User } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

// Payment QR options
const PAYMENT_OPTIONS = {
  primary: {
    id: 'primary',
    label: 'Saketh Sistla (UPI)',
    qrUrl: 'https://customer-assets.emergentagent.com/job_weakenders-rsvp/artifacts/gczl4the_WhatsApp%20Image%202026-02-13%20at%2016.14.04.jpeg',
    accountName: 'Saketh Sistla',
    upiId: '9930097428@slc'
  },
  gpay: {
    id: 'gpay',
    label: 'Saketh Sístla (GPay)',
    qrUrl: 'https://customer-assets.emergentagent.com/job_7e72cfbc-967d-41f6-a3b7-3dfbf6d375d8/artifacts/1mv4l1i3_image.png',
    accountName: 'Saketh Sístla',
    upiId: 'sístla.saketh@okhdfcbank'
  }
};

function AggregatePaymentPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [user, setUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactionRef, setTransactionRef] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payingWithWallet, setPayingWithWallet] = useState(false);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('primary');
  const [copied, setCopied] = useState(false);

  const currentPaymentOption = PAYMENT_OPTIONS[selectedPaymentOption];

  const handleCopyUPI = async () => {
    try {
      await navigator.clipboard.writeText(currentPaymentOption.upiId);
      setCopied(true);
      toast.success('UPI ID copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const [paymentsRes, userRes, walletRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments/user`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/wallet`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);
      // Filter only pending payments with positive amounts
      const pendingPayments = paymentsRes.data.filter(p => p.status === 'pending' && p.amount > 0);
      setPayments(pendingPayments);
      setUser(userRes.data);
      setWalletBalance(walletRes.data.wallet_balance || 0);
    } catch (error) {
      toast.error('Failed to load payments');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePayWithWallet = async () => {
    setPayingWithWallet(true);
    try {
      const paymentIds = payments.map(p => p.payment_id);
      const response = await axios.post(
        `${API_URL}/api/payments/pay-with-wallet`,
        { payment_ids: paymentIds },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      if (response.data.fully_paid) {
        toast.success(response.data.message);
        navigate('/dashboard');
      } else {
        // Wallet partially used - refresh to show updated amounts
        toast.success(response.data.message);
        setWalletBalance(0);
        // Refetch payments to get updated amounts
        await fetchPayments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use wallet');
    } finally {
      setPayingWithWallet(false);
    }
  };

  const handleMarkAllPaid = async () => {
    if (!transactionRef.trim()) {
      toast.error('Please enter transaction reference');
      return;
    }

    setSubmitting(true);
    try {
      // Mark all pending payments as paid
      await Promise.all(
        payments.map(payment =>
          axios.post(
            `${API_URL}/api/payments/mark-paid`,
            { payment_id: payment.payment_id, transaction_ref: transactionRef },
            {
              withCredentials: true,
              headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
            }
          )
        )
      );
      toast.success('All payments confirmed! Thank you.');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to submit payments');
    } finally {
      setSubmitting(false);
    }
  };

  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-50 bg-white border-b border-slate-200 pt-[env(safe-area-inset-top)]">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-[#0F172A]">Payments</h1>
          </div>
        </div>
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-2">All Caught Up!</h2>
          <p className="text-slate-600">You have no pending payments.</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="mt-6 bg-[#E63946] hover:bg-[#DC2626]"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="icon"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-[#0F172A]">Clear Your Dues</h1>
          </div>
          {/* User info with wallet balance */}
          <div className="flex items-center justify-between mt-3 px-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">{user?.nickname || user?.name}</span>
            </div>
            {walletBalance > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full">
                <Wallet className="w-3 h-3 text-green-600" />
                <span className="text-xs font-bold text-green-700">Wallet: ₹{walletBalance.toFixed(0)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Total Amount */}
        <motion.div
          className="bg-gradient-to-br from-[#E63946] to-[#DC2626] rounded-xl shadow-lg p-6 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center">
            <p className="text-sm text-white/80 mb-1">Total Amount Due</p>
            <p className="text-5xl font-bold" data-testid="total-amount">₹{totalAmount.toFixed(0)}</p>
            <p className="text-sm text-white/80 mt-2">
              {payments.length} {payments.length === 1 ? 'match' : 'matches'}
            </p>
          </div>
        </motion.div>

        {/* Wallet Payment Option */}
        {walletBalance > 0 && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-full">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-green-800">Pay with Wallet</h3>
                <p className="text-sm text-green-600">Balance: ₹{walletBalance.toFixed(0)}</p>
              </div>
            </div>
            
            {walletBalance >= totalAmount ? (
              <>
                <p className="text-sm text-green-700 mb-4">
                  Your wallet balance covers the full amount!
                </p>
                <Button
                  onClick={handlePayWithWallet}
                  disabled={payingWithWallet}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                  data-testid="pay-with-wallet-btn"
                >
                  {payingWithWallet ? 'Processing...' : `Pay ₹${totalAmount.toFixed(0)} from Wallet`}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-green-700 mb-4">
                  Click below to use your wallet balance. The remaining ₹{(totalAmount - walletBalance).toFixed(0)} can be paid via UPI.
                </p>
                <Button
                  onClick={handlePayWithWallet}
                  disabled={payingWithWallet}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                  data-testid="use-wallet-btn"
                >
                  {payingWithWallet ? 'Processing...' : `Use ₹${walletBalance.toFixed(0)} from Wallet`}
                </Button>
              </>
            )}
          </div>
        )}
        {/* Payment Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Payment Breakdown
          </h3>
          <div className="space-y-3">
            {payments.map((payment, idx) => (
              <motion.div
                key={payment.payment_id}
                className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <div>
                  <p className="font-semibold text-[#0F172A]">
                    {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
                    {payment.match?.venue && ` • ${payment.match.venue}`}
                  </p>
                </div>
                <p className="text-lg font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-[#E63946] p-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <QrCode className="w-5 h-5 text-[#E63946]" />
            <h2 className="text-lg font-bold text-[#0F172A]">Scan to Pay</h2>
          </div>

          {/* Radio Button Selection */}
          <div className="mb-4">
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">
              Select Payment Method
            </Label>
            <RadioGroup
              value={selectedPaymentOption}
              onValueChange={setSelectedPaymentOption}
              className="flex flex-col gap-2"
              data-testid="payment-method-radio-group"
            >
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#E63946] transition-colors cursor-pointer"
                   onClick={() => setSelectedPaymentOption('primary')}>
                <RadioGroupItem value="primary" id="primary" data-testid="payment-option-primary" />
                <Label htmlFor="primary" className="cursor-pointer flex-1">
                  <span className="font-medium text-[#0F172A]">{PAYMENT_OPTIONS.primary.label}</span>
                  <span className="block text-xs text-slate-500">{PAYMENT_OPTIONS.primary.upiId}</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#E63946] transition-colors cursor-pointer"
                   onClick={() => setSelectedPaymentOption('gpay')}>
                <RadioGroupItem value="gpay" id="gpay" data-testid="payment-option-gpay" />
                <Label htmlFor="gpay" className="cursor-pointer flex-1">
                  <span className="font-medium text-[#0F172A]">{PAYMENT_OPTIONS.gpay.label}</span>
                  <span className="block text-xs text-slate-500">{PAYMENT_OPTIONS.gpay.upiId}</span>
                </Label>
              </div>
            </RadioGroup>
          </div>
          
          {/* Dynamic QR Code Display */}
          <motion.div
            key={selectedPaymentOption}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-slate-50 rounded-lg p-4 mb-4"
          >
            <img 
              src={currentPaymentOption.qrUrl}
              alt={`${currentPaymentOption.label} QR Code`}
              className="w-full max-w-[280px] mx-auto"
              data-testid="payment-qr-code"
            />
          </motion.div>

          <div className="text-center space-y-1 bg-slate-50 rounded-lg p-3">
            <p className="text-sm font-semibold text-slate-700">Account: {currentPaymentOption.accountName}</p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-sm text-slate-600">UPI ID: {currentPaymentOption.upiId}</p>
              <button
                onClick={handleCopyUPI}
                className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
                data-testid="copy-upi-btn"
                title="Copy UPI ID"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-slate-500" />
                )}
              </button>
            </div>
            <p className="text-lg font-bold text-[#E63946] mt-2">Pay ₹{totalAmount.toFixed(0)}</p>
          </div>
        </div>

        {/* Payment Confirmation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">After Payment</h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="transaction_ref" className="text-sm font-semibold text-slate-700">
                Transaction Reference / UTR Number
              </Label>
              <Input
                id="transaction_ref"
                placeholder="e.g., 1234 or TXN123456"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                className="mt-2"
                data-testid="transaction-ref-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter transaction ID or last 4 digits for verification
              </p>
            </div>

            <Button
              onClick={handleMarkAllPaid}
              disabled={submitting || !transactionRef.trim()}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase py-6 rounded-lg"
              data-testid="mark-all-paid-btn"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  I Have Paid ₹{totalAmount.toFixed(0)}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              Your payment will be confirmed instantly after you submit
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AggregatePaymentPage;
